/**
 * Sprint 23.6.1 — AgentSpecialtiesPills.
 *
 * Sidebar Specialties section. Reads agent_config.specialties and renders
 * each as an info-coloured tag pill. Display-only: header (and section
 * container) deep-link to Settings tab Persona section for editing.
 */

import { type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router";
import type { AgentConfig, LibraryNodeDetail } from "../../../lib/types";
import { useThemedLabels } from "../../hooks/use-themed-labels";

export interface AgentSpecialtiesPillsProps {
  node: LibraryNodeDetail;
}

export function AgentSpecialtiesPills({ node }: AgentSpecialtiesPillsProps) {
  const labels = useThemedLabels();
  const navigate = useNavigate();
  const location = useLocation();
  const config = (node.agent_config ?? null) as AgentConfig | null;
  const specialties = config?.specialties ?? [];

  function goToPersona() {
    navigate(
      { pathname: location.pathname, hash: "#settings" },
      { state: { settingsSection: "persona" } },
    );
  }

  return (
    <aside style={wrap}>
      <button type="button" style={titleBtn} onClick={goToPersona}>
        {labels.sidebarSpecialtiesLabel}
      </button>
      {specialties.length === 0 ? (
        <div style={empty}>No specialties yet</div>
      ) : (
        <div style={pills}>
          {specialties.map((s, i) => (
            <span key={`${i}:${s}`} style={pill}>
              {s}
            </span>
          ))}
        </div>
      )}
    </aside>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: "12px 14px",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-secondary)",
};

const titleBtn: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  background: "transparent",
  border: "none",
  padding: 0,
  textAlign: "left",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const pills: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
};

const pill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: 11,
  padding: "3px 8px",
  borderRadius: 999,
  background: "#E6F1FB",
  color: "#0C447C",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
};

const empty: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  fontStyle: "italic",
};
